// RPT-1 — admin /reports visibility gating.
//
// The org-wide report library (`/reports`) aggregates across the whole org. A user who is
// scope-restricted by the scoped-roles system (one or more `role_scopes` rows narrowing them
// to specific groups/ministries/campuses) must NOT be shown org-wide aggregations — that would
// leak data beyond their scope. Such users self-serve their own slice via the leader Reports
// page instead. This predicate decides who keeps the full org-wide library.
//
// Admins/owners and unrestricted staff (no restricting scope rows) are unaffected.
// Per-row scope-filtering of the individual admin reports is a separate, deferred follow-up;
// this gate is access/visibility only.

export interface ReportAccessInput {
  /** Platform/org super admin — always org-wide. */
  isSuperAdmin: boolean;
  /** Organization owner/admin — always org-wide. */
  isOrgAdmin: boolean;
  /** Count of `role_scopes` rows that restrict this user. 0 = unrestricted (org-wide). */
  scopeCount: number;
  /**
   * Whether the `role_scopes` lookup actually succeeded. When the scope query
   * was denied or errored, `scopeCount` is an unreliable 0 and must NOT be read
   * as "unrestricted" — this gate FAILS CLOSED for non-admins in that case
   * (treats them as scoped) so a lookup failure can never widen access.
   */
  scopesReliable: boolean;
}

/**
 * True when the viewer may see the full org-wide report library. False for a scope-restricted
 * non-admin, who is routed to their leader Reports instead (no org-wide leakage).
 *
 * Admins/owners and super admins are always org-wide (they don't depend on scope data). A
 * non-admin is org-wide only when we RELIABLY determined they have zero restricting scopes;
 * if the scope lookup failed, fail closed.
 */
export function hasOrgWideReportAccess({
  isSuperAdmin,
  isOrgAdmin,
  scopeCount,
  scopesReliable,
}: ReportAccessInput): boolean {
  if (isSuperAdmin || isOrgAdmin) return true;
  return scopesReliable && scopeCount === 0;
}
